import React, { useMemo, useState } from "react";
import {
  Box,
  ExpandableSection,
  LineChart,
  MixedLineBarChartProps,
  SegmentedControl,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { DlqSizePoint, Stage } from "../../lusca-search-api/generated-src";
import { useDlqSizeHistory } from "../../hooks/useDlqSizeHistory";

/** Windows offered on the chart; ≤ 14 days is hourly, longer is one point per day. */
export const DLQ_SIZE_RANGES = [
  { id: "7", text: "7 days" },
  { id: "30", text: "30 days" },
  { id: "90", text: "90 days" },
  { id: "365", text: "1 year" },
] as const;

export const DLQ_SIZE_SERIES_TITLE = "Items in the DLQ";

const formatHour = (d: Date) =>
  d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
const formatDay = (d: Date) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function dlqSizeSeries(
  points?: DlqSizePoint[] | null
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  const data = (points ?? [])
    .map((p) => ({ x: new Date(p.t), y: p.n }))
    .filter((d) => !Number.isNaN(d.x.getTime()));
  return data.length
    ? [{ title: DLQ_SIZE_SERIES_TITLE, type: "line", data }]
    : [];
}

/** Latest minus first point of the window; null with fewer than two points. */
export function dlqSizeChange(points?: DlqSizePoint[] | null): number | null {
  if (!points || points.length < 2) return null;
  return points[points.length - 1].n - points[0].n;
}

const SOURCE_NOTE =
  "Every item in the DLQ table, investigated or not (CloudWatch ItemCount, refreshed every few hours).";

/** "446,856 items now, +6,856 over 30 days. <source>"; just the source note before data arrives. */
export function describeDlqSize(
  latest: number | null,
  change: number | null,
  rangeLabel: string
): string {
  if (latest === null) return SOURCE_NOTE;
  let text = `${latest.toLocaleString()} items now`;
  if (change !== null) {
    const sign = change > 0 ? "+" : "";
    text += `, ${sign}${change.toLocaleString()} over ${rangeLabel}`;
  }
  return `${text}. ${SOURCE_NOTE}`;
}

function chartStatus(
  failed: boolean,
  loading: boolean
): "error" | "loading" | "finished" {
  if (failed) return "error";
  return loading ? "loading" : "finished";
}

/**
 * Whole-DLQ size over time on the home page: the DLQ table's CloudWatch ItemCount, the same
 * population the per-investigation "Population over time" charts slice. DynamoDB refreshes the
 * count every few hours, so the line moves in steps.
 */
export const DlqSizeChart: React.FC<{ stage: Stage }> = ({ stage }) => {
  const [days, setDays] = useState<string>("30");
  const history = useDlqSizeHistory(stage, Number(days));
  const points = history.data?.points;
  const series = useMemo(() => dlqSizeSeries(points), [points]);
  const hourly = (history.data?.periodSeconds ?? 3600) < 86400;
  const formatTime = hourly ? formatHour : formatDay;
  const latest = points?.length ? points[points.length - 1].n : null;
  const change = dlqSizeChange(points);
  const rangeLabel =
    DLQ_SIZE_RANGES.find((r) => r.id === days)?.text ?? `${days} days`;

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="DLQ size over time"
      headerDescription={describeDlqSize(latest, change, rangeLabel)}
      headerActions={
        <SegmentedControl
          selectedId={days}
          onChange={({ detail }) => setDays(detail.selectedId)}
          label="Time range"
          options={DLQ_SIZE_RANGES.map((r) => ({ id: r.id, text: r.text }))}
        />
      }
    >
      <LineChart<Date>
        series={series}
        xScaleType="time"
        height={180}
        hideFilter
        hideLegend
        statusType={chartStatus(!!history.error, history.isLoading)}
        errorText={history.error?.message ?? "Could not load the DLQ size"}
        loadingText="Loading DLQ size"
        ariaLabel="Items in the DLQ over time"
        xTitle={hourly ? "Hour" : "Day"}
        yTitle="Items"
        xTickFormatter={formatTime}
        i18nStrings={{
          xTickFormatter: formatTime,
          detailPopoverDismissAriaLabel: "Dismiss",
          legendAriaLabel: "Legend",
          chartAriaRoleDescription: "line chart",
        }}
        detailPopoverSeriesContent={({ series: s, y }) => ({
          key: s.title,
          value: typeof y === "number" ? y.toLocaleString() : "",
        })}
        empty={
          <Box textAlign="center" color="inherit">
            <StatusIndicator type="info">
              No DLQ size recorded for this range.
            </StatusIndicator>
          </Box>
        }
      />
    </ExpandableSection>
  );
};

export default DlqSizeChart;
